import { Component, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { BehaviorSubject, forkJoin, lastValueFrom, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Campaign, CampaignLocationFilter, CampaignTargetUser, CampaignsCampaignLocationFilterAdminApiClient, LinkedCampaignDto, LinkedCampaignType } from 'src/app/api/api-campaigns.service.generated';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MultiLabelsComponent } from 'src/app/common/components/multi-labels/multi-labels.component';
import { ButtonComponent } from "wecheer-common";

@Component({
  selector: 'app-advertiser-linked-campaigns-edit',
  templateUrl: './advertiser-linked-campaigns-edit.component.html',
  styleUrls: ['./advertiser-linked-campaigns-edit.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatButtonModule,
    MatIconModule,
    MultiLabelsComponent,
    ButtonComponent
  ]
})
export class AdvertiserLinkedCampaignsEditComponent implements OnInit {
  dialogRef = inject<MatDialogRef<AdvertiserLinkedCampaignsEditComponent>>(MatDialogRef);
  data = inject<{
    title: string;
    campaigns: Campaign[];
    initData?: LinkedCampaignDto | undefined;
  }>(MAT_DIALOG_DATA);
  private locationFilterClient = inject(CampaignsCampaignLocationFilterAdminApiClient);

  locationDiffWarning = false;


  form = new FormGroup({
    name: new FormControl('', [Validators.required, Validators.maxLength(255)]),
    type: new FormControl(null, [Validators.required]),
    campaigns: new FormControl([], [Validators.required]),
  });
  filteredCampaigns = [];
  allCampaignsSubject: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  allCampaigns = this.allCampaignsSubject.asObservable();
  linkedTypes = [
    {
      value: LinkedCampaignType.Linked,
      name: LinkedCampaignType.Linked,
    },
    {
      value: LinkedCampaignType.Consecutive,
      name: LinkedCampaignType.Consecutive,
    },
  ]
  titleType = 'Add';
  maxArrayLengthError = false;
  wrongSelectError = false;
  staffCampaignStrategies = ["StaffObsolete", "StaffPointStrategy", "OpenerStrategy"];

  campaignBadgeFn = (campaign: Campaign): { text: string; cssClass: string } | null => {
    if (!campaign) return null;
    if (campaign.isConsumerReferralStrategy) {
      return { text: 'Consumer Ref', cssClass: 'badge-consumer-referral' };
    }
    if (campaign.isReferralCampaignStrategy) {
      return { text: 'Staff Ref', cssClass: 'badge-staff-referral' };
    }
    if (this.staffCampaignStrategies.includes(campaign?.strategy?.name ?? '')
      || campaign?.strategy?.name === 'StaffInformationStrategy'
      || campaign?.targetUser === CampaignTargetUser.Staff) {
      return { text: 'Staff', cssClass: 'badge-staff' };
    }
    return { text: 'Consumer', cssClass: 'badge-consumer' };
  };
  constructor() {
    const data = this.data;

    this.titleType = data.title;
    this.initializeCampaigns(data.campaigns, data.initData);
    this.initializeForm(data.initData);
  }

  private initializeCampaigns(campaigns: Campaign[], initData?: LinkedCampaignDto) {
    if (initData) {
      const filteredCampaigns = this.filterCampaigns(campaigns, initData.campaigns);
      this.allCampaignsSubject.next(filteredCampaigns);
    } else {
      this.allCampaignsSubject.next(campaigns);
    }
  }

  private initializeForm(initData?: LinkedCampaignDto) {
    if (initData) {
      this.form = new FormGroup({
        name: new FormControl(initData.name, [Validators.required, Validators.maxLength(255)]),
        type: new FormControl(initData.type.toString(), [Validators.required]),
        campaigns: new FormControl(initData.campaigns, [Validators.required]),
      });
    }
  }

  ngOnInit(): void {
    this.form.get('campaigns').valueChanges.subscribe(async newCampaigns => {
      const campaigns = (newCampaigns || []).map(Campaign.fromJS);
      this.allCampaignsSubject.next(this.filterCampaigns(this.data?.campaigns, campaigns));
      // Check location diff live
      this.locationDiffWarning = await this.checkLocationDiff(campaigns);
    });
  }

  private extractFilterKey(lf: CampaignLocationFilter | null | undefined): string {
    if (!lf?.filters?.length) return '';
    const ids: string[] = [];
    for (const f of lf.filters as any[]) {
      if (f?.organizationIds) ids.push(...f.organizationIds);
      else if (f?.outletIds) ids.push(...f.outletIds);
      else if (f?.tags) ids.push(...(f.tags as any[]).map((t: any) => `${t.key}:${t.value}`));
    }
    return [...new Set(ids)].sort().join(',');
  }

  private async checkLocationDiff(campaigns: Campaign[]): Promise<boolean> {
    if (campaigns.length < 2) return false;
    const filters = await lastValueFrom(
      forkJoin(campaigns.map(c =>
        this.locationFilterClient.getByCampaignId(c.campaignId).pipe(catchError(() => of(null)))
      ))
    );
    const keys = filters.map(lf => this.extractFilterKey(lf));
    return new Set(keys).size > 1;
  }

  static async open(dialog: MatDialog, data: { title: string, campaigns: Campaign[], initData?: LinkedCampaignDto | undefined }): Promise<LinkedCampaignDto | undefined> {
    const result = await lastValueFrom(dialog.open(AdvertiserLinkedCampaignsEditComponent, { width: '600px', data }).afterClosed());
    return result as (LinkedCampaignDto | undefined);
  }

  private filterCampaigns(campaigns: Campaign[], initData?: Campaign[]): Campaign[] {
    if (!initData?.length) {
      return campaigns;
    }

    const { targetUser, strategy } = initData[0];
    const strategyName = strategy?.name;

    const isStrategyMatching = (campaignStrategy: any, strategyName: string | undefined) => {
      return campaignStrategy?.name === strategyName;
    };

    const isPunchCardStaffRoleMatching = (strategy: any, targetUser: CampaignTargetUser) =>
      isStrategyMatching(strategy, "PunchCardObsolete") && targetUser === CampaignTargetUser.Staff;

    // StaffInformationStrategy targets staff when targetUser is Staff or unspecified
    const isStaffInformationStaffRoleMatching = (strategy: any, targetUser: CampaignTargetUser) =>
      isStrategyMatching(strategy, "StaffInformationStrategy") && (targetUser === CampaignTargetUser.Staff || !targetUser);

    const isStaffReferralCampaign = (c: Campaign) =>
      c?.isReferralCampaignStrategy === true && !c?.isConsumerReferralStrategy;

    const isStaffCampaign = (c: Campaign) =>
      this.staffCampaignStrategies.includes(c?.strategy?.name ?? "")
      || isPunchCardStaffRoleMatching(c?.strategy, c?.targetUser)
      || isStaffInformationStaffRoleMatching(c?.strategy, c?.targetUser);

    const isFirstStaffReferral = isStaffReferralCampaign(initData[0]);

    return campaigns.filter(campaign => {
      // If the first selected campaign is a staff referral, allow staff referral + regular staff campaigns
      if (isFirstStaffReferral) {
        return isStaffReferralCampaign(campaign) || isStaffCampaign(campaign);
      }

      // If the first selected campaign is any staff campaign (incl. StaffInformationStrategy), allow all staff campaigns + staff referrals
      if (isStaffCampaign(initData[0])) {
        return isStaffCampaign(campaign) || isStaffReferralCampaign(campaign);
      }

      if (strategyName !== "PunchCardObsolete") { // If the strategy is not "PunchCardObsolete"

        // Check if the strategy is "ConsumerInformationStrategy" and the target user is Consumer or not specified
        if (strategyName === "ConsumerInformationStrategy" && (targetUser === CampaignTargetUser.Consumer || !targetUser)) {
          // Ensure that the campaign strategy is either "PunchCardObsolete" or "ConsumerInformationStrategy" and that the campaign's target user is Consumer or not specified
          return (campaign?.strategy?.name === "PunchCardObsolete" || campaign?.strategy?.name === "ConsumerInformationStrategy")
            && (campaign?.targetUser === CampaignTargetUser.Consumer || !campaign?.targetUser);
        }

        // Default case: Check if the campaign strategy matches the given strategy name
        return isStrategyMatching(campaign.strategy, strategyName);

      } else { // If the strategy is "PunchCardObsolete" and target user is Consumer or not specified (staff case handled above by isStaffCampaign)

        // Check if the target user is Consumer or not specified
        if (targetUser === CampaignTargetUser.Consumer || !targetUser) {
          // Ensure that the campaign strategy is either "PunchCardObsolete" or "ConsumerInformationStrategy" and that the campaign's target user is Consumer or not specified
          return (campaign?.strategy?.name === "PunchCardObsolete" || campaign?.strategy?.name === "ConsumerInformationStrategy")
            && (campaign?.targetUser === CampaignTargetUser.Consumer || !campaign?.targetUser);
        }
      }

      return false;
    });
  }

  private validateForm(): boolean {
    const campaigns = this.form.value.campaigns || [];

    if (this.form.invalid) return false;
    if (campaigns.length > 5) {
      this.maxArrayLengthError = true;
      return false;
    }
    if (campaigns.some(item => typeof item !== 'object')) {
      this.wrongSelectError = true;
      return false;
    }

    this.maxArrayLengthError = false;
    this.wrongSelectError = false;
    return true;
  }

  onSave() {
    this.form.markAsTouched();

    if (this.validateForm()) {
      const type = LinkedCampaignType[this.form.value.type as keyof typeof LinkedCampaignType];
      const campaigns = (this.form.value.campaigns || []).map(Campaign.fromJS);

      this.dialogRef.close(new LinkedCampaignDto({
        name: this.form.value.name,
        type,
        campaigns
      }));
    }
  }

  displayFn(product: any): string {
    return product && product.name ? product.name : '';
  }
}
